import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-neutral-800 text-neutral-300" role="contentinfo">
      <div className="container-app py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 bg-gradient-to-br from-primary-400 to-primary-500 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M16 7h.01" />
                  <path d="M3.4 18H12a8 8 0 0 0 8-8V7a4 4 0 0 0-7.28-2.3L2 20" />
                  <path d="m20 7 2 .5-2 .5" />
                  <path d="M10 18v3" />
                  <path d="M14 17.75V21" />
                  <path d="M7 18a6 6 0 0 0 3.84-10.61" />
                </svg>
              </div>
              <span className="text-xl font-heading font-bold text-white">
                Roost<span className="text-primary-400">ing</span>
              </span>
            </Link>
            <p className="text-neutral-400 text-sm max-w-sm mb-4">
              {t('common.tagline')}
            </p>
            <p className="text-neutral-500 text-sm">{t('footer.madeWith')}</p>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-4">{t('footer.company')}</h3>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-sm hover:text-primary-400 transition-colors">{t('footer.aboutUs')}</Link></li>
              <li><Link to="/press" className="text-sm hover:text-primary-400 transition-colors">{t('footer.press')}</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-4">{t('footer.support')}</h3>
            <ul className="space-y-2">
              <li><Link to="/help" className="text-sm hover:text-primary-400 transition-colors">{t('footer.helpCenter')}</Link></li>
              <li><Link to="/safety" className="text-sm hover:text-primary-400 transition-colors">{t('footer.safetyCenter')}</Link></li>
              <li><Link to="/community" className="text-sm hover:text-primary-400 transition-colors">{t('footer.community')}</Link></li>
              <li><Link to="/contact" className="text-sm hover:text-primary-400 transition-colors">{t('common.contact')}</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-white font-semibold text-sm mb-4">{t('footer.legal')}</h3>
            <ul className="space-y-2">
              <li><Link to="/terms" className="text-sm hover:text-primary-400 transition-colors">{t('footer.termsOfService')}</Link></li>
              <li><Link to="/privacy" className="text-sm hover:text-primary-400 transition-colors">{t('footer.privacyPolicy')}</Link></li>
              <li><Link to="/cookies" className="text-sm hover:text-primary-400 transition-colors">{t('footer.cookiePolicy')}</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-8 border-t border-neutral-700 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-neutral-500 text-sm">
            {t('footer.copyright', { year: currentYear })}
          </p>

          {/* Social links */}
          <div className="flex items-center gap-4">
            <a href="https://twitter.com/roostingnyc" target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="text-neutral-400 hover:text-primary-400 transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
            </a>
            <a href="https://facebook.com/roostingnyc" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-neutral-400 hover:text-primary-400 transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
            </a>
            <a href="https://instagram.com/roostingnyc" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-neutral-400 hover:text-primary-400 transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
